import React from 'react'
import { Button, Form, Input, InputNumber, Modal, UploadFile } from 'antd'
import { vacanciesApi } from 'api/vacancies'
import { IVacancy } from 'models/vacancies'
import { Uploader } from 'components/ui/Uploader/Uploader'
import { IMAGES_URL } from 'shared/constants/urls'
import { showServerError } from 'shared/utils/showServerError'
import { showMessage } from 'shared/utils/showMessage'

// одно поле координат в формате "широта, долгота" → { lat, lng }; null если формат неверный
const parseCoordinates = (value?: string): { lat: number; lng: number } | null => {
  if (!value) return null
  const parts = value.split(',').map((p) => p.trim())
  if (parts.length !== 2) return null
  const lat = Number(parts[0])
  const lng = Number(parts[1])
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
  return { lat, lng }
}

const getFormValues = (vacancy: IVacancy | null) => {
  if (!vacancy) return {}
  return {
    name: vacancy.name,
    description: vacancy.description,
    salary: vacancy.salary || null,
    phone: vacancy.phone,
    contact_name: vacancy.contact_name,
    address: vacancy.location.name,
    coordinates: `${vacancy.location.lat}, ${vacancy.location.lng}`,
    photos: vacancy.photos.map((photo, i) => ({
      uid: `-${i + 1}`,
      name: photo,
      status: 'done',
      url: `${IMAGES_URL}${photo}`
    }))
  }
}

interface Props {
  open: boolean
  // null — новая вакансия
  vacancy: IVacancy | null
  onClose: () => void
  onSaved: () => void
}

export const VacancyModal: React.FC<Props> = ({ open, vacancy, onClose, onSaved }) => {
  const [form] = Form.useForm()
  const [isSubmitting, setIsSubmitting] = React.useState(false)

  // инстанс формы переживает закрытие модалки — заполняем при каждом открытии
  React.useEffect(() => {
    if (open) {
      form.resetFields()
      form.setFieldsValue(getFormValues(vacancy))
    }
  }, [open, vacancy])

  const handleSubmit = async (values: any) => {
    // валидатор поля гарантирует формат
    const coords = parseCoordinates(values.coordinates)!
    // галерея: новые файлы → photos, оставленные → existing_photos (имя файла без адреса CDN)
    const fileList: UploadFile[] = values.photos ?? []
    const photos = fileList.map((f) => f.originFileObj).filter(Boolean) as File[]
    const existing_photos = fileList
      .filter((f) => !f.originFileObj && f.url)
      .map((f) => (f.url as string).replace(IMAGES_URL, ''))
    const payload = {
      name: values.name,
      description: values.description,
      salary: values.salary ?? 0,
      phone: values.phone,
      contact_name: values.contact_name,
      location: { name: values.address, ...coords }
    }
    try {
      setIsSubmitting(true)
      if (vacancy) {
        await vacanciesApi.updateVacancy(vacancy._id, { ...payload, photos, existing_photos })
        showMessage('success', 'Вакансия сохранена', '')
      } else {
        await vacanciesApi.createVacancy({ ...payload, photos })
        showMessage('success', 'Вакансия опубликована', 'Она уже видна на сайте в разделе «Вакансии»')
      }
      onSaved()
    } catch (e) {
      showServerError(e)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal
      title={vacancy ? 'Редактировать вакансию' : 'Добавить вакансию'}
      open={open}
      onCancel={onClose}
      footer={null}
      width={720}
      destroyOnClose
    >
      <Form form={form} layout='vertical' onFinish={handleSubmit}>
        <Uploader
          name='photos'
          rules={[{ required: true, message: 'Добавьте фото' }]}
          type='variation'
          multiple
          sortable
          maxCount={10}
          hint='Фото вакансии (можно несколько; первое — обложка)'
        />

        <Form.Item label='Название' name='name' rules={[{ required: true, whitespace: true, message: 'Укажите название' }]}>
          <Input placeholder='Например, конюх' />
        </Form.Item>

        <Form.Item
          label='Описание'
          name='description'
          rules={[{ required: true, whitespace: true, message: 'Добавьте описание' }]}
        >
          <Input.TextArea rows={6} placeholder='Обязанности, требования, условия, график' />
        </Form.Item>

        <Form.Item label='Зарплата, ₽' name='salary' extra='Пусто — на сайте будет «По договорённости»'>
          <InputNumber min={0} precision={0} style={{ width: '100%' }} placeholder='По договорённости' />
        </Form.Item>

        <div style={{ display: 'flex', gap: 16 }}>
          <Form.Item
            label='Телефон'
            name='phone'
            rules={[{ required: true, whitespace: true, message: 'Укажите телефон' }]}
            style={{ flex: 1 }}
          >
            <Input placeholder='+7 (495) 085-10-30' />
          </Form.Item>
          <Form.Item
            label='Контактное лицо'
            name='contact_name'
            rules={[{ required: true, whitespace: true, message: 'Укажите контактное лицо' }]}
            style={{ flex: 1 }}
          >
            <Input placeholder='Имя' />
          </Form.Item>
        </div>

        <Form.Item label='Адрес' name='address' rules={[{ required: true, whitespace: true, message: 'Укажите адрес' }]}>
          <Input placeholder='Где работать' />
        </Form.Item>

        <Form.Item
          label='Координаты (широта, долгота)'
          name='coordinates'
          rules={[
            { required: true, message: 'Укажите координаты' },
            {
              validator: (_, value) =>
                !value || parseCoordinates(value)
                  ? Promise.resolve()
                  : Promise.reject(new Error('Формат: 55.941460, 37.272310'))
            }
          ]}
          extra='Для кнопки «Показать на карте». Скопируйте из Яндекс Карт: широта и долгота через запятую'
        >
          <Input placeholder='55.941460, 37.272310' />
        </Form.Item>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={onClose}>Отмена</Button>
          <Button type='primary' htmlType='submit' loading={isSubmitting}>
            {vacancy ? 'Сохранить' : 'Опубликовать'}
          </Button>
        </div>
      </Form>
    </Modal>
  )
}
